import { useContext } from 'react'
import { prettyBitcoinAmount, prettyBitcoinHide, prettyFiatAmount, prettyFiatHide } from '../lib/format'
import { ConfigContext } from '../providers/config'
import { FiatContext } from '../providers/fiat'
import FeesIcon from '../icons/Fees'
import AmountIcon from '../icons/Amount'
import TotalIcon from '../icons/Total'
import DateIcon from '../icons/Date'
import DirectionIcon from '../icons/Direction'
import TypeIcon from '../icons/Type'
import WhenIcon from '../icons/When'
import NotesIcon from '../icons/Notes'
import Table, { TableData } from './Table'
import StatusIcon from '../icons/Status'
import HashIcon from '../icons/Hash'
import InfoIcon from '../icons/Info'
import { Unit, Wallet } from '../lib/types'
import {
  openInNewTab,
  openOffchainTxInNewTab,
  openAssetInNewTab,
  getOffchainTxURL,
  getAssetURL,
} from '../lib/explorers'

export interface DetailsProps {
  address?: string
  arknote?: string
  assetId?: string
  date?: string
  destination?: string
  direction?: string
  expiry?: string
  fees?: number
  invoice?: string
  isOffchainTx?: boolean
  satoshis?: number
  status?: string
  swapId?: string
  total?: number
  txid?: string
  type?: string
  wallet?: Wallet
  when?: string
}

export default function Details({ details, variant }: { details?: DetailsProps; variant?: 'default' | 'receipt' }) {
  const { config, useFiat } = useContext(ConfigContext)
  const { toFiat } = useContext(FiatContext)

  if (!details) return <></>

  const {
    address,
    arknote,
    assetId,
    date,
    direction,
    destination,
    expiry,
    fees,
    invoice,
    isOffchainTx,
    satoshis,
    status,
    swapId,
    txid,
    type,
    total,
    wallet,
    when,
  } = details

  const formatAmount = (amount?: number) => {
    if (amount === undefined) return ''
    if (useFiat) {
      const fiat = toFiat(amount)
      return config.showBalance
        ? prettyFiatAmount(fiat, config.fiat, { bitcoinUnit: config.currencyDisplay })
        : prettyFiatHide(fiat, config.fiat, { bitcoinUnit: config.currencyDisplay })
    }
    return config.showBalance
      ? prettyBitcoinAmount(amount, config.currencyDisplay as unknown as Unit)
      : prettyBitcoinHide(amount, config.currencyDisplay)
  }

  // Only show explorer link if URL is available (e.g., mainnet for vmempool)
  const txidOnClick =
    wallet && txid
      ? () => {
          if (isOffchainTx) {
            openOffchainTxInNewTab(txid, wallet)
          } else {
            openInNewTab(txid, wallet)
          }
        }
      : undefined

  // Hide offchain tx link if vmempool URL not configured for this network
  const showTxidLink = txidOnClick && (!isOffchainTx || getOffchainTxURL(txid ?? '', wallet!))

  const assetIdOnClick =
    wallet && assetId && getAssetURL(assetId, wallet)
      ? () => {
          openAssetInNewTab(assetId, wallet)
        }
      : undefined

  const data: TableData = [
    ['Address', address, <TypeIcon key='address-icon' />],
    ['Arknote', arknote, <NotesIcon key='notes-icon' small />],
    ['Invoice', invoice, <TypeIcon key='invoice-icon' />],
    ['Swap ID', swapId, <InfoIcon key='swap-id-icon' />],
    ['Destination', destination, <TypeIcon key='destination-icon' />],
    ['Transaction ID', txid, <HashIcon key='txid-icon' />, showTxidLink ? txidOnClick : undefined],
    ['Asset ID', assetId, <InfoIcon key='asset-id-icon' />, assetIdOnClick],
    ['Direction', direction, <DirectionIcon key='direction-icon' />],
    ['Type', type, <TypeIcon key='type-icon' />],
    ['Status', status, <StatusIcon key='status-icon' />],
    ['When', when, <WhenIcon key='when-icon' />],
    ['Date', date, <DateIcon key='date-icon' />],
    ['Expiry', expiry, <DateIcon key='expiry-icon' />],
    ['Amount', formatAmount(satoshis), <AmountIcon key='amount-icon' />],
    ['Network fees', formatAmount(fees), <FeesIcon key='fees-icon' />],
    ['Total', formatAmount(total), <TotalIcon key='total-icon' />],
  ]

  return <Table data={data} variant={variant} />
}
